import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import ukLocale from '@angular/common/locales/uk';
import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { AuthEffects, AuthInterceptor, AuthService, authReducer } from '@lib/auth';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

import { environment } from '@admin/environments/environment';
import * as ClientCreateUiEffects from '@admin/store/client-create-ui/client-create-ui.effects';
import { clientCreateUiReducer } from '@admin/store/client-create-ui/client-create-ui.reducer';
import { insurancePackagesReducer } from '@admin/store/insurance-packages/insurance-packages.reducer';
import * as partnerEffects from '@admin/store/partner/partner.effects';
import { partnerReducer } from '@admin/store/partner/partner.reducer';

import { provideTranslateModule } from '@lib/core/translate/translate.provider';

import { AUTH_API_URL, PROTECTED_API_URL } from '../../../../packages/core/src/tokens/api.tokens';
import { routes } from './app.routes';

registerLocaleData(ukLocale);

export function initializeTokens(authService: AuthService) {
  return () => authService.initializeTokens();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideStore({
      auth: authReducer,
      insurancePackages: insurancePackagesReducer,
      clientCreateUi: clientCreateUiReducer,
      partner: partnerReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideEffects([AuthEffects, ClientCreateUiEffects, partnerEffects]),
    provideHttpClient(withInterceptors([AuthInterceptor])),
    provideTranslateModule(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTokens,
      deps: [AuthService],
      multi: true,
    },
    { provide: AUTH_API_URL, useValue: environment.authorizationApiUrl },
    { provide: PROTECTED_API_URL, useValue: environment.protectedApiUrl },
    { provide: 'DICTIONARIES_API_URL', useValue: environment.dictionariesApiUrl },
    { provide: LOCALE_ID, useValue: 'uk' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }, // Set your desired default appearance
    },
    provideNativeDateAdapter(),
  ],
};
